<template>
  <BaseFetchData :action="loadPage">
    <template #default="fetchDataProps">
      <BaseVeeForm
        v-if="fetchDataProps.status.resolved"
        :action="updateTags"
        ensure-error-keys-camel-case
        v-slot="{ isPending, invalid }"
        class="space-y-8"
      >
        <ValidationProvider name="tags" slim vid="tags">
          <template #default="{ errors }">
            <ul class="space-y-8">
              <TagCategory
                v-for="tagCategory in tagCategories"
                :key="tagCategory.id"
                :tag-category="tagCategory"
                :tags="tags"
              >
                <template #default="slotProps">
                  <TagOption
                    v-for="tag in slotProps.tags"
                    :key="tag.id"
                    :tag="tag"
                    v-model="contactTagIds"
                    :inherited="contactRequestTagIds.indexOf(tag.id) >= 0"
                    :disabled="isPending"
                  />
                </template>
              </TagCategory>
            </ul>

            <div v-if="errors.length" class="rounded-md bg-red-50 p-4 mt-8">
              <div class="flex">
                <div class="flex-shrink-0">
                  <!-- Heroicon name: solid/x-circle -->
                  <svg
                    class="h-5 w-5 text-red-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414
                      1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293
                      1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0
                      00-1.414-1.414L10 8.586 8.707 7.293z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-red-800">
                    There were {{ errors.length }} error(s) with your submission
                  </h3>
                  <div class="mt-2 text-sm text-red-700">
                    <ul role="list" class="list-disc pl-5 space-y-1">
                      <li v-for="(msg, idx) in errors" :key="idx">
                        {{ msg }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </ValidationProvider>
        <div class="flex space-x-2">
          <BaseButton primary :disabled="invalid || isPending">
            {{ $t('global.update') }}
          </BaseButton>
        </div>
      </BaseVeeForm>
    </template>
  </BaseFetchData>
</template>

<script>
import {
  accountTagCategoriesClient,
  accountTagsClient,
  contactContactRequestTagsClient,
  contactTagsClient,
} from '@/api/clients';
import mapKeysToCamelCase from '@/utils/map-keys-to-camel-case';
import mapKeysToSnakeCase from '@/utils/map-keys-to-snake-case';
import TagCategory from './TagCategory.vue';
import TagOption from './TagOption.vue';

export default {
  components: {
    TagCategory,
    TagOption,
  },
  props: {
    contactId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tagCategories: [],
      tags: [],
      contactTagIds: [],
      contactRequestTagIds: [],
    };
  },
  computed: {
    contact() {
      return this.$store.state.entities.contacts.byId[this.contactId];
    },
  },
  methods: {
    async loadPage() {
      try {
        await Promise.all([
          this.fetchAccountTagCategories(),
          this.fetchAccountTags(),
          this.fetchContactTags(),
          this.fetchContactContactRequestTags(),
        ]);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchAccountTagCategories() {
      try {
        const response = await accountTagCategoriesClient.list(
          this.contact.accountId,
          mapKeysToSnakeCase({
            filter: {
              taggableBy: ['contacts', 'contact_requests'],
            },
          })
        );

        this.tagCategories = response.data.map(mapKeysToCamelCase);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchAccountTags() {
      try {
        const response = await accountTagsClient.list(this.contact.accountId, {
          filter: {
            taggable_by: ['contacts', 'contact_requests'],
          },
          page_size: 500,
        });

        this.tags = response.data.map(mapKeysToCamelCase);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchContactTags() {
      try {
        const response = await contactTagsClient.list(this.contactId);

        this.setContactTags(response.data);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchContactContactRequestTags() {
      try {
        const response = await contactContactRequestTagsClient.list(this.contactId);

        this.setContactRequestTags(response.data);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async updateTags() {
      try {
        const response = await contactTagsClient.store(this.contactId, {
          tags: this.contactTagIds,
        });

        this.setContactTags(response.data);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setContactTags(tags) {
      this.contactTagIds = tags.map((tag) => tag.id);
    },
    setContactRequestTags(tags) {
      this.contactRequestTagIds = tags.map((tag) => tag.id);
    },
  },
};
</script>
